import dayjs from "dayjs";
import isToday from "dayjs/plugin/isToday";
import isYesterday from "dayjs/plugin/isYesterday";
import type { ChatResultItem } from "shared/types";

dayjs.extend(isToday);
dayjs.extend(isYesterday);

export type ChatListItem =
  | { kind: "date-separator"; date: string; key: string }
  | {
      kind: "message";
      message: ChatResultItem;
      key: string;
      isFirstInGroup: boolean;
      isLastInGroup: boolean;
    };

function getDateKey(date: string): string {
  if (dayjs(date).isToday()) return "Today";
  if (dayjs(date).isYesterday()) return "Yesterday";
  return dayjs(date).format("DD MMM YYYY");
}

// ── Sender grouping pass ──────────────────────────────────────────

/**
 * Mark adjacent messages from the same sender within 15 min as grouped.
 * Timeline messages are excluded — they don't use grouping flags.
 */
function applySenderGrouping(items: ChatListItem[]): void {
  const GROUP_TIME_THRESHOLD_MS = 15 * 60000;

  for (let i = 1; i < items.length; i++) {
    const curr = items[i];
    const prev = items[i - 1];

    if (curr.kind !== "message" || prev.kind !== "message") continue;

    if (
      curr.message.message_type === "member_timeline" ||
      prev.message.message_type === "member_timeline"
    )
      continue;

    const sameSender =
      curr.message.sender.mapping_id === prev.message.sender.mapping_id;

    const timeDiff = Math.abs(
      new Date(curr.message.created_at).getTime() -
        new Date(prev.message.created_at).getTime(),
    );

    const sameThread =
      (curr.message.parent_message_id ?? null) ===
        (prev.message.parent_message_id ?? null) &&
      (curr.message.thread_id ?? null) === (prev.message.thread_id ?? null);

    if (sameSender && sameThread && timeDiff <= GROUP_TIME_THRESHOLD_MS) {
      curr.isFirstInGroup = false;
      prev.isLastInGroup = false;
    }
  }
}

// ── Main builder ──────────────────────────────────────────────────

/**
 * Convert newest-first sorted messages into a flat oldest-first list
 * with date separator items interleaved.
 *
 * Virtuoso renders top → bottom = oldest → newest.
 */
export function buildFlatChatList(
  sortedMessages: ChatResultItem[],
): ChatListItem[] {
  if (sortedMessages.length === 0) return [];

  // sortedMessages is newest-first, reverse to get oldest-first
  const oldestFirst = [...sortedMessages].reverse();

  const items: ChatListItem[] = [];
  let lastDateKey = "";

  for (const msg of oldestFirst) {
    const dateKey = getDateKey(msg.created_at);

    if (dateKey !== lastDateKey) {
      items.push({
        kind: "date-separator",
        date: dateKey,
        key: `sep-${dateKey}`,
      });
      lastDateKey = dateKey;
    }

    items.push({
      kind: "message",
      message: msg,
      key: msg.chat_id,
      isFirstInGroup: true,
      isLastInGroup: true,
    });
  }

  applySenderGrouping(items);

  return items;
}

/** Find the index of a message in the flat list by chat_id */
export function findMessageIndex(
  flatList: ChatListItem[],
  chatId: string,
): number {
  return flatList.findIndex(
    (item) => item.kind === "message" && item.message.chat_id === chatId,
  );
}
